import { Children, useContext, useEffect, useState } from 'react';
import { Grid, Paper, Divider, Box, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { profileActionCreators } from 'redux/actionCreators';
import {
    CardTitleComponent,
    ProfileInfoRowVaraint2Component,
    ProfileInfoRowComponent,
    NoRecordsComponent,
} from 'components';
import { trainingSelector } from 'redux/selectors';
import { trainingActionType } from 'redux/actionsTypes';
import {
    IProfileTrainingObject,
    IReduxDataConfig,
} from '../../../@types/profile-types';
import { toLocalDateString } from 'util/date.utils';
import { TrainingForm } from './trainingForm';
import { getSingleLineAddressFromObject } from 'util/displayAddress';
import { ThemeContext } from 'util/themes/themeContext';

const styles = {
    cardPadding: {
        p: 3,
    },
    gridWrapper: { mb: 3, flexGrow: 0 },
};

interface ITrainingComponent {
    title: string;
    canEdit: boolean;
    type: 'PREVIEW' | 'PAGE';
}

const convertObjectToProfileRowVariant = (object: IProfileTrainingObject, selectedTheme: any) => {
    const header = object.name;
    const subHeader = object.organizationName || undefined;
    const addressLine = getSingleLineAddressFromObject(object);
    const bodyList = [];
    if (addressLine) {
        bodyList.push(
            <Typography
                sx={{
                    fontSize: '12px',
                    ...selectedTheme?.typography?.captions?.default,
                    ...selectedTheme?.typography?.captions?.bold,
                    color: selectedTheme?.palette?.secondary?.grayScale && selectedTheme?.palette?.secondary?.grayScale[800]
                }}
                key={'form_address_line'}>
                {addressLine}
            </Typography>
        );
    }
    return { header, subHeader, bodyList };
};

export const TrainingCardComponent = (props: ITrainingComponent) => {
    const dispatch = useDispatch();
    const [modalOpen, setModalOpen] = useState(false);
    const [modalEditData, setModalEditData] = useState(null);
    const { title, canEdit, type } = props;
    const { selectedTheme } = useContext(ThemeContext);

    const trainingLoading = useSelector(trainingSelector.selectTrainingLoading);
    const trainingList = useSelector(trainingSelector.selectTrainingData);

    const trainingConfig: IReduxDataConfig = {
        loadingAction: trainingActionType.PROFILE_TRAINING_LOADING,
        successAction: trainingActionType.PROFILE_TRAINING_SUCCESS,
        errorAction: trainingActionType.PROFILE_TRAINING_ERROR,
        endpoint: '/learnerserver/training',
    };

    useEffect(() => {
        dispatch(
            profileActionCreators.getPersonalDynamicCardsInfo(trainingConfig)
        );
    }, []);

    const handleModalOpen = (id: string) => {
        const find = trainingList.find(
            (x: IProfileTrainingObject) => x.asset_id === id
        );
        setModalOpen(true);
        setModalEditData(find ? JSON.parse(JSON.stringify(find)) : null);
    };

    const handleModalClose = () => {
        setModalOpen(false);
    };

    const addTraining = () => {
        setModalOpen(true);
        setModalEditData(null);
    };

    return (
        <Grid item xs sx={styles.gridWrapper}>
            <Paper sx={{...selectedTheme.card.default, border:`1px solid ${selectedTheme.palette.primary.light}`}}>
                <CardTitleComponent
                    title={title}
                    canAdd={type === 'PAGE' ? 'ACTIVE' : 'HIDDEN'}
                    onAddIconClicked={addTraining}
                />
                <Divider />
                <Grid container sx={styles.cardPadding}>
                    {trainingList.length === 0 && !trainingLoading ? (
                        <NoRecordsComponent />
                    ) : (
                        <> </>
                    )}
                    {Children.toArray(
                        trainingList.map(
                            (x: IProfileTrainingObject,index:number) => {
                                const row = convertObjectToProfileRowVariant(x, selectedTheme);
                                return (
                                    <Box className="flex-column full-width">
                                        <ProfileInfoRowVaraint2Component
                                            isLoading={trainingLoading}
                                            header={row.header}
                                            subHeader={row.subHeader}
                                            bodyDataList={row.bodyList}
                                            objectId={x.asset_id}
                                            onEditClicked={handleModalOpen}
                                            canEdit={
                                                canEdit ? 'ACTIVE' : 'HIDDEN'
                                            }
                                        />
                                        <Grid container direction="row">
                                            <Grid item xs={6} lg={6}>
                                                <ProfileInfoRowComponent
                                                    isLoading={trainingLoading}
                                                    label="Start Date"
                                                    value={
                                                        toLocalDateString(
                                                            x.startDate?.$date
                                                        ) || ''
                                                    }
                                                    canEdit={'HIDDEN'}
                                                />
                                            </Grid>
                                            <Grid item xs={6} lg={6}>
                                                <ProfileInfoRowComponent
                                                    isLoading={trainingLoading}
                                                    label="End Date"
                                                    value={
                                                        toLocalDateString(
                                                            x.endDate?.$date
                                                        ) || ''
                                                    }
                                                    canEdit={'HIDDEN'}
                                                />
                                            </Grid>
                                        </Grid>
                                        {x.hours ? (
                                            <ProfileInfoRowComponent
                                                isLoading={trainingLoading}
                                                label="Hours"
                                                value={x.hours}
                                                canEdit={'HIDDEN'}
                                            />
                                        ) : (
                                            <></>
                                        )}
                                        {index !== trainingList.length-1 && <Divider sx={{marginBottom:'10px'}}/>}
                                    </Box>
                                );
                            }
                        )
                    )}
                </Grid>
            </Paper>
            {modalOpen ? (
                <TrainingForm
                    modalOpen={modalOpen}
                    trainingReduxConfig={trainingConfig}
                    handleModalClose={handleModalClose}
                    modalEditData={modalEditData}
                />
            ) : (
                <> </>
            )}
        </Grid>
    );
};

TrainingCardComponent.defaultProps = {
    canEdit: true,
    title: '',
    type: 'PAGE',
};
